import axios from 'axios'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import { jwtDecode } from 'jwt-decode'
import i18n from '@/plugins/i18n'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import type IJwt from '@/models/Authentication/Jwt'
import router from '@/router'

// Prefix all axios requests with /api/
const instance = axios.create({
  baseURL: '/api/',
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
})

instance.interceptors.request.use(
  config => {
    // Block all requests if we're redirecting to login or unauthorised
    if (isRedirectingToLogin || isRedirectingToUnauthorised) {
      return Promise.reject(new Error('Redirecting to login - blocking request'))
    }

    const token = localStorage.getItem('token')

    if (config.headers) {
      config.headers.Authorization = token ? `Bearer ${token}` : ''
    }

    // Add locale query param
    const locale = localStorage.getItem('locale') || 'en'
    if (!config.params) {
      config.params = {}
    }
    config.params.locale = locale

    return config
  },
)

/**
 * Redirects user to login page, preserving the current page path for redirect after login
 */
function redirectToLogin() {
  // Set flag to block all future requests
  isRedirectingToLogin = true
  window.sessionStorage.setItem('requested_page', window.location.pathname + window.location.search + window.location.hash)
  window.location.href = '/auth/login'
}

/**
 * Checks for multi-tab shadowing org mismatch
 * Returns true if mismatch detected and handled
 */
function checkShadowingOrgMismatch(authStore: ReturnType<typeof useAuthStore>): boolean {
  if (!authStore.is_shadowing || !authStore.selected_organisation_id) {
    return false
  }

  const token = localStorage.getItem('token')
  if (!token) {
    return false
  }

  try {
    const jwt: IJwt = jwtDecode(token)
    if (jwt.selected_organisation_id !== authStore.selected_organisation_id) {
      // Token is for different org - multi-tab shadowing interference
      // Page reload is simplest - restoreJwtClaims will sync state on reload
      window.location.reload()

      return true
    }
  }
  catch (error) {
    // Token decode failed, let normal error handling proceed
    return false
  }

  return false
}

// Track ongoing refresh attempts to prevent race conditions
let isRefreshing = false
let refreshPromise: Promise<void> | null = null
let isRedirectingToLogin = false
let isRedirectingToUnauthorised = false

interface ExtendedFailedRequest {
  response: { status: number; config: { headers: { Authorization: string | null }; url?: string } }
  _orgBeforeRefresh?: number
}

// Catches 401/403 errors and attempts silent token refresh
async function refreshAuthLogic(failedRequest: ExtendedFailedRequest) {
  const authStore = useAuthStore()
  const status = failedRequest.response.status
  const url = failedRequest.response.config.url || ''

  // Handle 403 - Check for multi-tab shadowing org mismatch
  if (status === 403) {
    // Check if this is due to multi-tab shadowing interference
    if (checkShadowingOrgMismatch(authStore)) {
      return Promise.reject(new Error('Multi-tab shadowing org mismatch detected'))
    }

    // Legitimate 403 - account disabled or access revoked mid-session.
    // Logout and navigate to unauthorised page without surfacing an error.
    isRedirectingToUnauthorised = true
    authStore.logout()

    // If user has multiple profiles, redirect to profile selector
    if (authStore.user_profiles.length > 0) {
      router.push({ name: 'auth.user-profile-selector' })
    } else {
      router.push({ name: 'auth.unauthorised' })
    }

    return Promise.reject()
  }

  // Skip refresh if this is already a refresh token request to prevent loops
  if (url.includes('/oauth/token-with-session')) {
    // This is a token refresh request itself - refresh token is invalid/expired
    // Silently redirect to login
    redirectToLogin()

    // Return rejected promise to stop a retry
    return Promise.reject(new Error('Token refresh request failed - redirecting to login'))
  }

  // Handle shadow requests specially
  if (url.includes('/api/myhq-user-shadow')) {
    // For shadow requests, we need to check if org changed after refresh
    // If it did, don't retry - redirect to login to avoid wrong data
    const tokenBeforeRefresh = localStorage.getItem('token')
    if (tokenBeforeRefresh) {
      try {
        const jwtBefore: IJwt = jwtDecode(tokenBeforeRefresh)
        const orgBeforeRefresh = jwtBefore.selected_organisation_id

        // Continue with normal refresh logic below, but we'll check after
        // Store the org for comparison after refresh
        failedRequest._orgBeforeRefresh = orgBeforeRefresh
      }
      catch (error) {
        // Token decode failed, continue with normal refresh
      }
    }
  }

  // If already refreshing, wait for that refresh to complete
  if (isRefreshing && refreshPromise) {
    await refreshPromise

    // Token should be refreshed now, axios-auth-refresh will retry the request
    return
  }

  // Mark that we're starting a refresh
  isRefreshing = true
  refreshPromise = (async () => {
    try {
      // Attempt silent token refresh
      await authStore.refreshToken()

      // Verify token was saved
      const token = localStorage.getItem('token')
      if (!token) {
        // No token after refresh - this shouldn't happen, but handle it
        if (!authStore.hasRefreshToken) {
          redirectToLogin()
          throw new Error('Token refresh failed - redirecting to login')
        }
        throw new Error('Token refresh failed - no token saved')
      }

      // Check if this was a shadow request and if org changed during refresh
      const orgBeforeRefresh = failedRequest._orgBeforeRefresh
      if (orgBeforeRefresh) {
        const jwt: IJwt = jwtDecode(token)
        if (jwt.selected_organisation_id !== orgBeforeRefresh) {
          // Org changed during shadow request refresh - don't retry with wrong org
          // Let the error bubble up so router guard can retry loadUserData() with fresh state
          throw new Error('SHADOW_ORG_MISMATCH')
        }
      }

      // Token refreshed successfully - axios-auth-refresh will automatically retry the request
      // The request interceptor will pick up the new token from localStorage
    }
    catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      const isNoTokenSaved = errorMessage.includes('no token saved')
      const isNoRefreshToken = errorMessage.includes('No refresh token available')

      const isTokenRequestFailed = axios.isAxiosError(error)
        && error.config?.url?.includes('token-with-session')

      // "no token saved" indicates refresh succeeded but token wasn't saved - this is a bug
      if (isNoTokenSaved) {
        // eslint-disable-next-line no-console
        console.error('[Axios] refreshAuthLogic - Token refresh succeeded but token not saved', error)
      }

      // Refresh failed: no token, or server rejected it (e.g. session refreshed elsewhere when shadowing)
      // Remove token and redirect to login to get fresh tokens
      if (isNoRefreshToken || isTokenRequestFailed || !authStore.hasRefreshToken) {
        authStore.removeRefreshToken()
        redirectToLogin()

        throw error
      }
      // Re-throw to prevent retry
      throw error
    }
    finally {
      // Reset the refresh state
      isRefreshing = false
      refreshPromise = null
    }
  })()

  await refreshPromise
}

// 401s handled in refresh auth logic, 404s handled in individual components
// Catch and handle Response Status'
instance.interceptors.response.use(
  response => {
    return response.data
  },
  error => {
    const appStore = useAppStore()

    if (error.response?.status === 400) {
      appStore.setSnackbar({
        text: (error.response.data.message) ? error.response.data.message : i18n.global.t('axios.error_message.400'),
        colour: 'error',
      })
    }
    else if (error.response?.status === 500) {
      appStore.setSnackbar({
        text: (error.response.data.message) ? error.response.data.message : i18n.global.t('axios.error_message.500'),
        colour: 'error',
      })
    }

    return Promise.reject(error)
  },
)

// Instantiate the interceptor
createAuthRefreshInterceptor(instance, refreshAuthLogic, { 'statusCodes': [401, 403] })

export default instance
